import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Download } from "lucide-react";

import TruncatedWithTooltip from "../../../TruncatedWithTooltip";
import { UIButton, UITable } from "../../../ui";
import { clx } from "../../../../utils/Helpers";
import {
  formatClientReportDate,
  formatClientReportSize,
  getClientReportFileTypeLabel,
} from "./clientReportMappers";

import "../Reports/ReportsShared.scss";
import "./ClientReports.scss";

const EMPTY_CELL = "-";

const emptyCell = () => (
  <span className="MuiDataGrid-cellContent">{EMPTY_CELL}</span>
);

/**
 * Cells inherit the page direction so they align to the row start in both
 * languages; `bdi` isolates the value so its own characters keep their order.
 * `dir="ltr"` pins machine-formatted values (`38 KB`) that must not reorder.
 */
const cellText = (value, dir) => {
  if (!value) return emptyCell();
  return (
    <div className="reports-cell-truncate">
      <TruncatedWithTooltip title={value}>
        <span className="MuiDataGrid-cellContent">
          <bdi dir={dir}>{value}</bdi>
        </span>
      </TruncatedWithTooltip>
    </div>
  );
};

const FileNameCell = ({ name, typeLabel }) => (
  <div className="client-reports__file-cell">
    <TruncatedWithTooltip title={name}>
      <span className="client-reports__file-name">
        <bdi>{name}</bdi>
      </span>
    </TruncatedWithTooltip>
    {typeLabel ? (
      <span className="client-reports__file-type">{typeLabel}</span>
    ) : null}
  </div>
);

FileNameCell.propTypes = {
  name: PropTypes.string,
  typeLabel: PropTypes.string,
};

/** `date` / `time` arrive display-ready from the API — only the order changes. */
const DateTimeCell = ({ date, time }) => {
  if (!date && !time) return emptyCell();

  return (
    <div className="reports-datetime">
      <span className="reports-datetime__date">
        <bdi dir="ltr">{formatClientReportDate(date)}</bdi>
      </span>
      {time ? (
        <span className="reports-datetime__time">
          <bdi dir="ltr">{time}</bdi>
        </span>
      ) : null}
    </div>
  );
};

DateTimeCell.propTypes = {
  date: PropTypes.string,
  time: PropTypes.string,
};

const ClientReportsTable = ({
  rows,
  labels,
  pageSize,
  canDelete,
  canPreview,
  onDownload,
  onDelete,
  onPreview,
}) => {
  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: labels.fileName,
        flex: 1.4,
        minWidth: 200,
        sortable: false,
        renderCell: (params) => (
          <FileNameCell
            name={params.value}
            typeLabel={getClientReportFileTypeLabel(
              params.row.fileType,
              labels.fileTypes,
            )}
          />
        ),
      },
      {
        field: "size",
        headerName: labels.size,
        flex: 0.5,
        minWidth: 100,
        sortable: false,
        renderCell: (params) =>
          cellText(formatClientReportSize(params.value), "ltr"),
      },
      {
        field: "uploaderName",
        headerName: labels.uploadedBy,
        flex: 1.1,
        minWidth: 180,
        sortable: false,
        // `creator` is null for Admin-Portal uploads — show a neutral label.
        renderCell: (params) => (
          <div className="client-reports__uploader">
            <TruncatedWithTooltip title={params.value || labels.unknownUploader}>
              <span className="client-reports__uploader-name">
                <bdi>{params.value || labels.unknownUploader}</bdi>
              </span>
            </TruncatedWithTooltip>
            {params.row.uploaderType ? (
              <span className="client-reports__uploader-role">
                {params.row.uploaderType}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        field: "date",
        headerName: labels.uploadDate,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => (
          <DateTimeCell date={params.value} time={params.row.time} />
        ),
      },
      {
        field: "actions",
        headerName: labels.actions,
        flex: 0.6,
        minWidth: 160,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => (
          <div className="client-reports__row-actions">
            <UIButton
              type="button"
              variant="outline"
              size="small"
              className="client-reports__download-btn"
              aria-label={labels.download}
              onClick={(event) => {
                // The row opens the preview — the Download action must not.
                event.stopPropagation();
                onDownload(params.row);
              }}
            >
              <Download size={16} aria-hidden />
            </UIButton>
            {/* `isDeletable` is server-evaluated for the caller — see PROLINE-3636. */}
            {canDelete && params.row.isDeletable ? (
              <UIButton
                type="button"
                variant="destructive"
                appearance="outline"
                size="small"
                className="client-reports__delete-btn"
                onClick={(event) => {
                  // Delete must not open the preview.
                  event.stopPropagation();
                  onDelete(params.row);
                }}
              >
                {labels.delete}
              </UIButton>
            ) : null}
          </div>
        ),
      },
    ],
    [labels, canDelete, onDownload, onDelete],
  );

  // Enter/Space on any cell but the Download action opens the preview.
  const handleCellKeyDown = (params, event) => {
    if (
      canPreview &&
      params.field !== "actions" &&
      (event.key === "Enter" || event.key === " ")
    ) {
      event.preventDefault();
      onPreview(params.row);
    }
  };

  return (
    <div
      className={clx(
        "reports-list-table client-reports__table",
        canPreview && "client-reports__table--clickable",
      )}
    >
      <UITable
        rows={rows}
        columns={columns}
        pageSize={pageSize}
        contentSized
        hideFooter
        disableSelectionOnClick
        getRowId={(row) => row.id}
        onRowClick={
          canPreview ? (params) => onPreview(params.row) : undefined
        }
        onCellKeyDown={handleCellKeyDown}
      />
    </div>
  );
};

ClientReportsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  labels: PropTypes.object.isRequired,
  pageSize: PropTypes.number.isRequired,
  canDelete: PropTypes.bool,
  /** Row click / keyboard opens the preview modal. Off for the read-only Admin view. */
  canPreview: PropTypes.bool,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
};

ClientReportsTable.defaultProps = {
  canDelete: true,
  canPreview: true,
  onPreview: undefined,
};

export default ClientReportsTable;
